import { Box } from "@mui/material";
import { ru } from "date-fns/locale";
import React, { useEffect, useRef, useState } from "react";
import { RangeCalendar } from "src/shared/components/fields/DateRangeField";
import { getCalendarRanges } from "src/shared/components/fields/DateRangeField/model/constants";
import { getCalendarState } from "src/shared/components/fields/DateRangeField/model/helpers";
import { ECalendarRange } from "src/shared/components/fields/DateRangeField/model/types";
import { IDateRange } from "src/shared/components/fields/DateRangeField/ui";
import {
  EFilterType,
  ICalendarFilterInfo,
  IFilterValue,
} from "../../../model/types";
import TableFilterSelect from "../TableFilterSelect";
import { getDateFormatted } from "./helpers/dateFormat.helpers";

interface ICalendarState {
  startDate: Date | null;
  endDate: Date | null;
  key: "selection";
}

interface ITableFilterCalendarProps {
  name: string;
  title: string;
  onChange: (v: IFilterValue<ICalendarFilterInfo>) => void;
  value: ICalendarFilterInfo;
  minDate?: Date | null;
  maxDate?: Date | null;
}

const TableFilterCalendar: React.FC<ITableFilterCalendarProps> = ({
  onChange,
  name,
  value,
  title,
  minDate,
  maxDate,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const anchorEl = useRef(null);
  const [calendarRange, setCalendarRange] = useState<ECalendarRange | string>(
    "",
  );
  const [state, setState] = useState<ICalendarState>(() => ({
    key: "selection",
    ...getCalendarState(value),
  }));

  useEffect(() => {
    if (value.startDate && value.endDate) {
      setState({ key: "selection", ...getCalendarState(value) });
    } else {
      setState({
        key: "selection",
        startDate: null,
        endDate: null,
      });
    }
  }, [value]);

  useEffect(() => {
    setCalendarRange(value.calendarRange || "");
  }, [value.calendarRange]);

  const isEmpty = !value.startDate && !value.endDate;

  const selectValue = isEmpty
    ? ""
    : calendarRange === ECalendarRange.CUSTOM
      ? state.key
      : calendarRange;

  const handleSelectOnChange = (calendarRangeValue: string) => {
    switch (calendarRangeValue) {
      case ECalendarRange.TODAY:
      case ECalendarRange.WEEK:
      case ECalendarRange.MONTH: {
        setCalendarRange(calendarRangeValue);
        const { range } = getCalendarRanges()[calendarRangeValue];
        const { startDate, endDate } = range();
        onChange({
          name,
          value: { startDate, endDate, calendarRange: calendarRangeValue },
        });
        break;
      }
      case ECalendarRange.CUSTOM: {
        setAnchor(anchorEl.current);
        break;
      }
      case ECalendarRange.ASAP: {
        setCalendarRange(calendarRangeValue);
        onChange({
          name,
          value: {
            startDate: null,
            endDate: null,
            calendarRange: calendarRangeValue,
          },
        });
        break;
      }

      default: {
        setCalendarRange(calendarRangeValue);
        onChange({
          name,
          value: {
            startDate: null,
            endDate: null,
            calendarRange: null,
          },
        });
      }
    }
  };

  const handleApply = (v: IDateRange) => {
    setCalendarRange(ECalendarRange.CUSTOM);
    onChange({
      name,
      value: {
        startDate: v.startDate,
        endDate: v.endDate,
        calendarRange: ECalendarRange.CUSTOM,
      },
    });
  };

  const handleCustomRenderValue = () => {
    return value.startDate && value.endDate
      ? getDateFormatted(value.startDate, value.endDate)
      : "";
  };

  return (
    <Box ref={anchorEl}>
      <TableFilterSelect
        customRenderValue={
          calendarRange === ECalendarRange.CUSTOM
            ? handleCustomRenderValue
            : undefined
        }
        filter={{
          title,
          name: "",
          value: selectValue,
          type: EFilterType.SINGLE_SELECT,
          options: [
            { name: "Сегодня", value: ECalendarRange.TODAY },
            {
              name: "Неделя",
              value: ECalendarRange.WEEK,
            },
            {
              name: "Месяц",
              value: ECalendarRange.MONTH,
            },
            {
              name: "Произвольный",
              value: ECalendarRange.CUSTOM,
            },
          ],
        }}
        onChange={({ value: selectValue }) => {
          handleSelectOnChange(selectValue as string);
        }}
      />
      <RangeCalendar
        locale={ru}
        anchor={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        value={getCalendarState(value)}
        onChange={handleApply}
        minDate={minDate ?? undefined}
        maxDate={maxDate ?? undefined}
      />
    </Box>
  );
};

export default TableFilterCalendar;
